/**
 * Configurator — Save builds to logged-in customer profile (via proxy worker)
 * Builds are scoped per product handle (X1, Cloudcap, etc.)
 */

(function () {
  'use strict';

  var DRAFT_DEBOUNCE_MS = 1500;
  var draftTimer = null;
  var cachedBuilds = { products: {} };
  var startOverDialog = null;
  var startOverTrigger = null;

  function settings() {
    return window.configuratorSettings || {};
  }

  function isLoggedIn() {
    var section = document.querySelector('.configurator[data-customer-logged-in="true"]');
    if (section) return true;
    return !!(settings().customerId && settings().buildsAuth);
  }

  function isAccountPage() {
    return !!document.getElementById('cfg-account-saved-builds-list');
  }

  function currentProductHandle() {
    return (settings().product && settings().product.handle) || '';
  }

  function apiBase() {
    var url = (settings().buildsApiUrl || '').replace(/\/+$/, '');
    return url || 'https://shopify-proxy-wranlger.cascadiamarquee.workers.dev/configurator/builds';
  }

  function authHeaders(productHandle) {
    var auth = settings().buildsAuth || {};
    var headers = {
      'Content-Type': 'application/json',
      'X-Customer-Id': String(settings().customerId || ''),
      'X-Auth-Timestamp': String(auth.timestamp || ''),
      'X-Auth-Signature': String(auth.signature || ''),
      'X-Shop-Domain': String(settings().shopDomain || ''),
    };
    if (productHandle) headers['X-Product-Handle'] = productHandle;
    return headers;
  }

  function apiConfigured() {
    return !!(settings().customerId && settings().buildsAuth && settings().buildsAuth.signature);
  }

  function productQuery(productHandle) {
    if (!productHandle) return '';
    return '?product=' + encodeURIComponent(productHandle);
  }

  function resolveProductHandle(build) {
    if (!build) return currentProductHandle();
    return build.productHandle || (build.product && build.product.handle) || currentProductHandle();
  }

  function getProductBucket(handle) {
    if (!handle) return { current: null, saved: [] };
    if (!cachedBuilds.products[handle]) {
      cachedBuilds.products[handle] = { current: null, saved: [] };
    }
    return cachedBuilds.products[handle];
  }

  function getScopedBuilds() {
    return getProductBucket(currentProductHandle());
  }

  function buildRecord(name) {
    var snapshot = {};
    if (window.ConfiguratorSerializer && typeof window.ConfiguratorSerializer.serializeForWebhook === 'function') {
      snapshot = window.ConfiguratorSerializer.serializeForWebhook();
    }

    var shareQuery = '';
    var shareUrl = window.location.href;
    if (window.ConfiguratorUrlState) {
      if (typeof window.ConfiguratorUrlState.buildShareQuery === 'function') {
        shareQuery = window.ConfiguratorUrlState.buildShareQuery() || '';
      }
      if (typeof window.ConfiguratorUrlState.buildShareUrl === 'function') {
        shareUrl = window.ConfiguratorUrlState.buildShareUrl();
      }
    }

    var vehicleLabel = '';
    if (snapshot.vehicle) {
      vehicleLabel = snapshot.vehicle.label || snapshot.vehicle.value || '';
    }

    var productHandle = (snapshot.product && snapshot.product.handle) || currentProductHandle();

    return {
      id: crypto.randomUUID ? crypto.randomUUID() : 'build_' + Date.now(),
      name: name || 'My Build',
      updatedAt: Date.now(),
      productHandle: productHandle,
      shareQuery: shareQuery,
      shareUrl: shareUrl,
      product: snapshot.product || null,
      vehicle: snapshot.vehicle || null,
      vehicleLabel: vehicleLabel,
      generation: snapshot.generation || null,
      installLocation: snapshot.installLocation || null,
      variant: snapshot.variant || null,
      addOns: snapshot.addOns || [],
      pricing: snapshot.pricing || null,
      snapshot: snapshot,
    };
  }

  function request(method, path, body, productHandle) {
    if (!apiConfigured()) {
      return Promise.reject(new Error('Profile saves are not configured'));
    }

    var handle = productHandle || resolveProductHandle(body);
    var query = productQuery(handle);
    var url = apiBase() + path + (path.indexOf('?') >= 0 ? '' : query);

    return fetch(url, {
      method: method,
      credentials: 'omit',
      headers: authHeaders(handle),
      body: body ? JSON.stringify(body) : undefined,
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) throw new Error(data.error || 'Request failed');
        return data;
      });
    });
  }

  function applyProductResponse(data) {
    if (data.products) {
      cachedBuilds.products = data.products;
      return cachedBuilds;
    }
    if (data.productHandle) {
      getProductBucket(data.productHandle).current = data.current || null;
      getProductBucket(data.productHandle).saved = Array.isArray(data.saved) ? data.saved : [];
    }
    return getScopedBuilds();
  }

  function fetchBuilds(productHandle) {
    var handle = productHandle || currentProductHandle();
    if (!handle && isAccountPage()) {
      return request('GET', '', null, '').then(applyProductResponse);
    }
    if (!handle) {
      return Promise.resolve({ current: null, saved: [] });
    }
    return request('GET', '', null, handle).then(function (data) {
      applyProductResponse(data);
      return getProductBucket(handle);
    });
  }

  function fetchAllBuilds() {
    return request('GET', '', null, '').then(applyProductResponse);
  }

  function saveDraft() {
    if (!isLoggedIn() || !apiConfigured() || !currentProductHandle()) return Promise.resolve();
    var record = buildRecord('Draft');
    record.name = 'Draft';
    return request('PUT', '/current', record, record.productHandle).then(function (data) {
      applyProductResponse(data);
      document.dispatchEvent(new CustomEvent('configurator:profileDraftSaved', { detail: { build: record } }));
      return record;
    }).catch(function (err) {
      console.warn('[Configurator] Draft save failed:', err.message);
    });
  }

  function scheduleDraftSave() {
    if (!isLoggedIn() || !currentProductHandle()) return;
    clearTimeout(draftTimer);
    draftTimer = setTimeout(saveDraft, DRAFT_DEBOUNCE_MS);
  }

  function saveNamedBuild(name) {
    var trimmed = (name || '').trim();
    if (!trimmed) return Promise.reject(new Error('Build name is required'));
    var record = buildRecord(trimmed);
    return request('POST', '/saved', record, record.productHandle).then(function (data) {
      applyProductResponse(data);
      renderMyBuildsLists();
      return record;
    });
  }

  function renameBuild(id, name, productHandle) {
    var trimmed = (name || '').trim();
    if (!trimmed) return Promise.reject(new Error('Build name is required'));
    var handle = productHandle || currentProductHandle();
    return request('PATCH', '/saved/' + encodeURIComponent(id), { name: trimmed }, handle).then(function (data) {
      applyProductResponse(data);
      renderMyBuildsLists();
      renderAccountBuildsList();
    });
  }

  function deleteBuild(id, productHandle) {
    var handle = productHandle || currentProductHandle();
    return request('DELETE', '/saved/' + encodeURIComponent(id), null, handle).then(function (data) {
      applyProductResponse(data);
      renderMyBuildsLists();
      renderAccountBuildsList();
    });
  }

  function clearDraft() {
    var handle = currentProductHandle();
    if (!handle) return Promise.resolve();
    return request('DELETE', '/current', null, handle).then(function (data) {
      applyProductResponse(data);
    });
  }

  function clearAllBuilds(productHandle) {
    var query = productHandle ? productQuery(productHandle) : '';
    return request('DELETE', '/all' + query, null, productHandle || '').then(function (data) {
      applyProductResponse(data);
      renderMyBuildsLists();
      renderAccountBuildsList();
    });
  }

  function loginUrlWithReturn() {
    var loginUrl = settings().accountLoginUrl || '/account/login';
    var returnTo = encodeURIComponent(window.location.pathname + window.location.search + window.location.hash);
    var sep = loginUrl.indexOf('?') >= 0 ? '&' : '?';
    return loginUrl + sep + 'return_to=' + returnTo;
  }

  function formatPrice(pricing) {
    if (!pricing || pricing.total == null) return '';
    return '$' + Number(pricing.total).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  function formatDate(ts) {
    if (!ts) return '';
    try {
      return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (e) {
      return '';
    }
  }

  function productTitleForHandle(handle, build) {
    if (build && build.product && build.product.title) return build.product.title;
    if (handle === 'x1-camper') return 'X1 Camper';
    if (handle === 'cloudcap') return 'Cloudcap';
    return handle.replace(/-/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  function buildOpenUrl(build) {
    if (build && build.shareUrl) return build.shareUrl;
    var handle = resolveProductHandle(build);
    if (build && build.shareQuery && handle) {
      return '/products/' + handle + '?' + build.shareQuery;
    }
    return null;
  }

  function renderBuildListItem(build, options) {
    var li = document.createElement('li');
    li.className = 'cfg-profile-builds__item';

    var openUrl = buildOpenUrl(build);
    var title = document.createElement('span');
    title.className = 'cfg-profile-builds__item-title';
    title.textContent = build.name || build.vehicleLabel || 'Build';

    var meta = document.createElement('span');
    meta.className = 'cfg-profile-builds__item-meta';
    var parts = [build.vehicleLabel || '', formatPrice(build.pricing), formatDate(build.updatedAt)].filter(Boolean);
    meta.textContent = parts.join(' · ');

    var actions = document.createElement('div');
    actions.className = 'cfg-profile-builds__item-actions';
    var productHandle = resolveProductHandle(build);

    if (openUrl) {
      var openBtn = document.createElement('a');
      openBtn.className = 'cfg-profile-builds__action';
      openBtn.href = openUrl;
      openBtn.textContent = options.openLabel || 'Open';
      actions.appendChild(openBtn);
    }

    if (options.showRename) {
      var more = document.createElement('details');
      more.className = 'cfg-profile-builds__more';
      more.innerHTML = '<summary class="cfg-profile-builds__action" aria-label="More actions">More</summary>';
      var menu = document.createElement('div');
      menu.className = 'cfg-profile-builds__more-menu';
      var renameBtn = document.createElement('button');
      renameBtn.type = 'button';
      renameBtn.className = 'cfg-profile-builds__action';
      renameBtn.textContent = options.renameLabel || 'Rename';
      renameBtn.addEventListener('click', function () {
        more.removeAttribute('open');
        var next = window.prompt('Rename build', build.name || '');
        if (next === null) return;
        renameBuild(build.id, next, productHandle).catch(function (err) {
          window.alert(err.message || 'Could not rename build');
        });
      });
      menu.appendChild(renameBtn);
      more.appendChild(menu);
      actions.appendChild(more);
    }

    if (options.showDelete) {
      var deleteBtn = document.createElement('button');
      deleteBtn.type = 'button';
      deleteBtn.className = 'cfg-profile-builds__action cfg-profile-builds__action--danger';
      deleteBtn.textContent = options.deleteLabel || 'Delete';
      deleteBtn.addEventListener('click', function () {
        if (!window.confirm('Delete this saved build?')) return;
        deleteBuild(build.id, productHandle).catch(function (err) {
          window.alert(err.message || 'Could not delete build');
        });
      });
      actions.appendChild(deleteBtn);
    }

    li.appendChild(title);
    li.appendChild(meta);
    li.appendChild(actions);
    return li;
  }

  function renderMyBuildsLists() {
    var scoped = getScopedBuilds();
    document.querySelectorAll('.js-cfg-my-builds-list').forEach(function (list) {
      list.innerHTML = '';
      if (!scoped.saved.length) {
        var empty = document.createElement('li');
        empty.className = 'cfg-profile-builds__empty';
        empty.textContent = 'No saved builds yet';
        list.appendChild(empty);
        return;
      }
      scoped.saved.forEach(function (build) {
        list.appendChild(renderBuildListItem(build, {
          showRename: true,
          showDelete: true,
        }));
      });
    });
  }

  function renderAccountBuildsList() {
    var container = document.getElementById('cfg-account-saved-builds-list');
    if (!container) return;

    container.innerHTML = '';
    var handles = Object.keys(cachedBuilds.products || {});
    if (!handles.length) {
      container.innerHTML = '<p class="cfg-profile-builds__empty">No saved builds yet. Configure a camper and use Save Build in the configurator.</p>';
      return;
    }

    handles.sort().forEach(function (handle) {
      var bucket = cachedBuilds.products[handle];
      var hasContent = (bucket.current && bucket.current.shareUrl) || (bucket.saved && bucket.saved.length);
      if (!hasContent) return;

      var section = document.createElement('div');
      section.className = 'cfg-profile-builds__product-group';

      var heading = document.createElement('h3');
      heading.className = 'cfg-profile-builds__product-title';
      heading.textContent = productTitleForHandle(handle, bucket.current || (bucket.saved && bucket.saved[0]));
      section.appendChild(heading);

      var ul = document.createElement('ul');
      ul.className = 'cfg-profile-builds__account-list';

      if (bucket.current && bucket.current.shareUrl) {
        ul.appendChild(renderBuildListItem(Object.assign({}, bucket.current, { name: 'Current draft' }), {
          openLabel: 'Continue',
          showRename: false,
          showDelete: false,
        }));
      }

      (bucket.saved || []).forEach(function (build) {
        ul.appendChild(renderBuildListItem(build, {
          showRename: true,
          showDelete: true,
        }));
      });

      section.appendChild(ul);
      container.appendChild(section);
    });

    if (!container.children.length) {
      container.innerHTML = '<p class="cfg-profile-builds__empty">No saved builds yet. Configure a camper and use Save Build in the configurator.</p>';
    }
  }

  function pageHasCfgParams() {
    if (window.ConfiguratorUrlState && typeof window.ConfiguratorUrlState.hasCfgParams === 'function') {
      if (window.ConfiguratorUrlState.hasCfgParams()) return true;
    }
    var search = window.location.search || window.__spConfiguratorBootSearch || '';
    return search.indexOf('cfg_v=') !== -1;
  }

  function applyDraftOnCurrentPage(shareUrl) {
    var target;
    try {
      target = new URL(shareUrl, window.location.origin);
    } catch (e) {
      return false;
    }

    var currentPath = window.location.pathname.replace(/\/$/, '') || '/';
    var targetPath = target.pathname.replace(/\/$/, '') || '/';
    if (currentPath !== targetPath) return false;

    var next = target.pathname + target.search;
    var current = window.location.pathname + window.location.search;
    if (next === current) return true;

    window.history.replaceState({}, '', next);
    window.__spConfiguratorBootSearch = target.search || '';

    if (window.ConfiguratorUrlState && window.ConfiguratorRestore && typeof window.ConfiguratorRestore.restore === 'function') {
      var state = window.ConfiguratorUrlState.parseStateFromUrl();
      if (state) window.ConfiguratorRestore.restore(state);
    }
    return true;
  }

  function restoreProfileDraftIfNeeded() {
    if (!isLoggedIn() || !apiConfigured() || !currentProductHandle()) return Promise.resolve();
    if (pageHasCfgParams()) return Promise.resolve();

    return fetchBuilds().then(function (data) {
      if (!data.current || !data.current.shareUrl) return;
      var currentBuildHandle = resolveProductHandle(data.current);
      if (currentBuildHandle && currentBuildHandle !== currentProductHandle()) return;
      if (pageHasCfgParams()) return;
      if (applyDraftOnCurrentPage(data.current.shareUrl)) return;
      window.location.replace(data.current.shareUrl);
    }).catch(function (err) {
      console.warn('[Configurator] Profile draft load failed:', err.message);
    });
  }

  function resetConfigurator() {
    if (window.ConfiguratorCart && typeof window.ConfiguratorCart.startOver === 'function') {
      window.ConfiguratorCart.startOver();
    }
  }

  function getStartOverDialog() {
    return document.getElementById('cfg-start-over-dialog');
  }

  function closeStartOverDialog() {
    var modal = startOverDialog || getStartOverDialog();
    if (!modal || modal.hasAttribute('hidden')) {
      startOverDialog = null;
      return;
    }
    if (typeof removeTrapFocus === 'function') removeTrapFocus();
    modal.classList.remove('is-open');
    modal.setAttribute('hidden', '');
    document.body.classList.remove('sp-modal-open');
    startOverDialog = null;
    if (startOverTrigger && typeof startOverTrigger.focus === 'function') {
      startOverTrigger.focus();
    }
  }

  function confirmStartOver() {
    var modal = startOverDialog || getStartOverDialog();
    var checkbox = modal ? modal.querySelector('.js-cfg-start-over-clear-draft') : null;
    var option = modal ? modal.querySelector('.js-cfg-start-over-draft-option') : null;
    var clearDraftRequested = !!(checkbox && option && !option.hidden && checkbox.checked);
    startOverTrigger = null;
    closeStartOverDialog();
    if (clearDraftRequested) {
      clearDraft().finally(resetConfigurator);
      return;
    }
    resetConfigurator();
  }

  function openStartOverDialog(trigger) {
    var modal = getStartOverDialog();
    if (!modal) {
      if (!window.confirm('Start over? Your current configuration will be reset.')) return;
      if (isLoggedIn() && apiConfigured() && window.confirm('Also clear your saved draft from your profile?')) {
        clearDraft().finally(resetConfigurator);
        return;
      }
      resetConfigurator();
      return;
    }

    var sheet = document.querySelector('.js-cfg-builds-sheet');
    if (sheet && sheet.open) closeBuildsSheet();

    startOverTrigger = trigger || document.activeElement;
    startOverDialog = modal;
    var option = modal.querySelector('.js-cfg-start-over-draft-option');
    var checkbox = modal.querySelector('.js-cfg-start-over-clear-draft');
    var cartNote = modal.querySelector('.js-cfg-start-over-cart-note');
    if (option) option.hidden = !(isLoggedIn() && apiConfigured());
    if (checkbox) checkbox.checked = false;
    if (cartNote) {
      var form = document.querySelector('product-form');
      var hasCartBuild = !!(form && form.boundCartKey);
      if (!hasCartBuild) {
        hasCartBuild = !!new URLSearchParams(window.location.search).get('cfg_ck');
      }
      cartNote.hidden = !hasCartBuild;
    }

    modal.classList.add('is-open');
    modal.removeAttribute('hidden');
    document.body.classList.add('sp-modal-open');

    var drawer = modal.querySelector('[role="dialog"]') || modal;
    var confirmBtn = modal.querySelector('.js-cfg-start-over-confirm');
    if (typeof trapFocus === 'function') {
      trapFocus(drawer, confirmBtn || drawer);
    } else if (confirmBtn) {
      confirmBtn.focus();
    }
  }

  function startOver(trigger) {
    openStartOverDialog(trigger);
  }

  var sheetClosing = false;
  var sheetCloseTimer = null;

  function setSheetOpen(open) {
    var trigger = document.querySelector('.js-cfg-builds-menu-trigger');
    if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    document.documentElement.classList.toggle('cfg-builds-sheet-open', open);
  }

  function markTriggerSaved() {
    var trigger = document.querySelector('.js-cfg-builds-menu-trigger');
    if (!trigger) return;
    trigger.classList.add('is-saved');
    setTimeout(function () { trigger.classList.remove('is-saved'); }, 2000);
  }

  function setSaveStatus(message, isError) {
    var status = document.querySelector('.js-cfg-builds-save-status');
    if (!status) return;
    if (!message) {
      status.hidden = true;
      status.textContent = '';
      return;
    }
    status.hidden = false;
    status.textContent = message;
    status.classList.toggle('is-error', !!isError);
  }

  function openBuildsSheet() {
    var sheet = document.querySelector('.js-cfg-builds-sheet');
    if (!sheet) return;

    if (sheetClosing) {
      sheetClosing = false;
      if (sheetCloseTimer) clearTimeout(sheetCloseTimer);
    }

    if (typeof sheet.showModal === 'function') {
      if (!sheet.open) sheet.showModal();
    } else {
      sheet.setAttribute('open', '');
    }
    setSheetOpen(true);
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        sheet.classList.add('is-open');
      });
    });

    var login = document.querySelector('.js-cfg-builds-login');
    if (login) login.href = loginUrlWithReturn();

    if (isLoggedIn() && apiConfigured()) {
      fetchBuilds().then(renderMyBuildsLists).catch(function () { /* ignore */ });
    }

    var nameInput = document.querySelector('.js-cfg-builds-save-name');
    if (nameInput) {
      setTimeout(function () { nameInput.focus(); }, 280);
    }
  }

  function finishCloseSheet(sheet) {
    sheet.classList.remove('is-open');
    if (typeof sheet.close === 'function' && sheet.open) {
      sheet.close();
    } else {
      sheet.removeAttribute('open');
    }
    sheetClosing = false;
    if (sheetCloseTimer) {
      clearTimeout(sheetCloseTimer);
      sheetCloseTimer = null;
    }
    setSaveStatus('');
  }

  function closeBuildsSheet() {
    var sheet = document.querySelector('.js-cfg-builds-sheet');
    if (!sheet || !sheet.open || sheetClosing) return;

    sheetClosing = true;
    sheet.classList.remove('is-open');
    setSheetOpen(false);

    var done = false;
    function finish() {
      if (done) return;
      done = true;
      sheet.removeEventListener('transitionend', onEnd);
      finishCloseSheet(sheet);
    }
    function onEnd(event) {
      if (event.target !== sheet) return;
      finish();
    }
    sheet.addEventListener('transitionend', onEnd);
    sheetCloseTimer = setTimeout(finish, 360);
  }

  function bindBuildsMenu() {
    var trigger = document.querySelector('.js-cfg-builds-menu-trigger');
    var sheet = document.querySelector('.js-cfg-builds-sheet');
    if (!trigger || !sheet) return;

    trigger.addEventListener('click', function (event) {
      event.preventDefault();
      if (sheet.open) {
        closeBuildsSheet();
        return;
      }
      openBuildsSheet();
    });

    var closeBtn = document.querySelector('.js-cfg-builds-sheet-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', function (event) {
        event.preventDefault();
        closeBuildsSheet();
      });
    }

    sheet.addEventListener('click', function (event) {
      if (event.target === sheet) closeBuildsSheet();
    });

    sheet.addEventListener('close', function () {
      sheet.classList.remove('is-open');
      setSheetOpen(false);
      sheetClosing = false;
    });

    sheet.addEventListener('cancel', function (event) {
      event.preventDefault();
      closeBuildsSheet();
    });

    var form = document.querySelector('.js-cfg-builds-save-form');
    if (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        if (!apiConfigured()) {
          setSaveStatus('Save to profile is not configured yet.', true);
          return;
        }
        var input = form.querySelector('.js-cfg-builds-save-name');
        var name = input ? input.value.trim() : '';
        if (!name) {
          setSaveStatus('Name this build to save it.', true);
          if (input) input.focus();
          return;
        }
        var submit = form.querySelector('.js-cfg-builds-save-submit');
        if (submit) submit.disabled = true;
        saveNamedBuild(name)
          .then(function () {
            if (input) input.value = '';
            setSaveStatus('Saved');
            markTriggerSaved();
            renderMyBuildsLists();
          })
          .catch(function (err) {
            setSaveStatus(err.message || 'Could not save build', true);
          })
          .finally(function () {
            if (submit) submit.disabled = false;
          });
      });
    }

    var startOverBtn = document.querySelector('.js-cfg-builds-start-over');
    if (startOverBtn) {
      startOverBtn.addEventListener('click', function (event) {
        event.preventDefault();
        startOver(startOverBtn);
      });
    }
  }

  function bindStartOverDialog() {
    var modal = getStartOverDialog();
    if (!modal) return;

    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }

    modal.addEventListener('click', function (event) {
      if (event.target === modal) closeStartOverDialog();
    });

    modal.querySelectorAll('.js-cfg-start-over-cancel').forEach(function (btn) {
      btn.addEventListener('click', function (event) {
        event.preventDefault();
        closeStartOverDialog();
      });
    });

    var confirmBtn = modal.querySelector('.js-cfg-start-over-confirm');
    if (confirmBtn) {
      confirmBtn.addEventListener('click', function (event) {
        event.preventDefault();
        confirmStartOver();
      });
    }

    document.addEventListener('keydown', function (event) {
      if (!startOverDialog) return;
      if (event.key !== 'Escape' && event.key !== 'Esc' && event.code !== 'Escape') return;
      event.preventDefault();
      closeStartOverDialog();
    }, true);
  }

  function bindAccountPage() {
    var clearBtn = document.querySelector('.js-cfg-clear-all-builds');
    if (clearBtn) {
      clearBtn.addEventListener('click', function (event) {
        event.preventDefault();
        if (!window.confirm('Clear all saved builds and drafts for every product? This cannot be undone.')) return;
        clearAllBuilds()
          .then(function () {
            renderAccountBuildsList();
          })
          .catch(function (err) {
            window.alert(err.message || 'Could not clear builds');
          });
      });
    }

    if (isAccountPage() && isLoggedIn()) {
      fetchAllBuilds()
        .then(function () {
          renderAccountBuildsList();
        })
        .catch(function () {
          var container = document.getElementById('cfg-account-saved-builds-list');
          if (container) container.innerHTML = '<p class="cfg-profile-builds__empty">Could not load saved builds.</p>';
        });
    }
  }

  function init() {
    bindBuildsMenu();
    bindStartOverDialog();
    bindAccountPage();

    document.addEventListener('configurator:stateSaved', scheduleDraftSave);

    document.addEventListener('configurator:ready', function () {
      restoreProfileDraftIfNeeded();
      if (isLoggedIn() && apiConfigured() && currentProductHandle()) {
        fetchBuilds().then(function () {
          renderMyBuildsLists();
        }).catch(function () { /* ignore */ });
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.ConfiguratorProfileBuilds = {
    fetchBuilds: fetchBuilds,
    fetchAllBuilds: fetchAllBuilds,
    saveDraft: saveDraft,
    clearDraft: clearDraft,
    saveNamedBuild: saveNamedBuild,
    renameBuild: renameBuild,
    deleteBuild: deleteBuild,
    clearAllBuilds: clearAllBuilds,
    buildRecord: buildRecord,
    currentProductHandle: currentProductHandle,
    loginUrlWithReturn: loginUrlWithReturn,
    startOver: startOver,
    isLoggedIn: isLoggedIn,
    apiConfigured: apiConfigured,
  };
})();
